import React, { useEffect, useState } from 'react'
import { MaterialIcon } from '../MaterialIcon/MaterialIcon'
import { MaterialIconId } from '../MaterialIcon/MaterialIconId.type'
import { MaterialIconSize } from '../MaterialIcon/MaterialIconSize.type'
import { SearchBox } from '../SearchBox/SearchBox'
import { MostVisitedLinks } from '../MostVisitedLinks/MostVisitedLinks'
import { SearchPanelProps } from '../SearchPanel/SearchPanel'
import { MenuItem } from '../../types/MenuItem.type'
import { AppMenuMobileMenuItem } from './AppMenuMobileMenuItem'
import './style.module.scss'
import { Link, LinkVariant } from '../../main'
import { AdvisorData } from '../../types/AdvisorData.type'
import { UserResponse } from '@ampcore/client-side-services'
import { UserStatus } from '../../types/UserStatus.enum'
import { makeUrlRelative } from '../../lib/CfgApi/makeUrlRelative'

export type AppMenuMobileProps = {
  userStatus?: UserStatus
  className?: string
  isOpen: boolean
  searchPanel?: SearchPanelProps
  menuItems?: MenuItem[]
  mobileBottomMenuItems: MenuItem[]
  mobileNavSectionRef: React.RefObject<HTMLDivElement>
  advisorData?: AdvisorData | null
  userData?: UserResponse | null
  testId?: string
}

export const AppMenuMobile = (props: AppMenuMobileProps) => {
  const {
    userStatus,
    className,
    isOpen,
    searchPanel,
    menuItems,
    mobileBottomMenuItems,
    mobileNavSectionRef,
    advisorData,
    userData,
    testId
  } = props
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [navigationTree, setNavigationTree] = useState<number[]>([])

  function getMenuItems(
    arr: MenuItem[],
    indexes: number[]
  ): MenuItem[] | undefined {
    let value = arr
    for (const index of indexes) {
      if (value[index].menuItems === undefined) {
        return undefined
      }
      value = value[index].menuItems!
    }
    return value
  }

  const handleSearchClick = () => {
    setIsSearchOpen(prev => !prev)
  }

  const handleBackClick = () => {
    setIsSearchOpen(false)
    setNavigationTree([])
  }

  const updateNavigationTree = (
    event: React.KeyboardEvent<HTMLAnchorElement> | React.MouseEvent,
    newIndexes: number[]
  ) => {
    setNavigationTree(previousIndexes => {
      const isSameIndex =
        JSON.stringify(previousIndexes) === JSON.stringify(newIndexes)

      // Collapse tier 2 menu on close
      if (newIndexes.length === 2 && isSameIndex) {
        event.preventDefault()
        return newIndexes.slice(0, -1)
      }

      const subMenuItems = getMenuItems(menuItems!, newIndexes)

      // Return previous indexes if there are no sub menu options
      if (subMenuItems?.length === 0) {
        return previousIndexes
      }

      // Expand tier 1 menu if it has children and is not open
      if (previousIndexes.length === 0 && subMenuItems) {
        event.preventDefault()
        return newIndexes
      }

      // Disable link if not tier 2 and has children
      if (
        !isSameIndex &&
        newIndexes.length !== 1 &&
        subMenuItems?.length !== 0
      ) {
        event.preventDefault()
      }

      return newIndexes
    })
  }

  const handleMenuKeyDown = (
    event: React.KeyboardEvent<HTMLAnchorElement>,
    indexes: number[]
  ) => {
    if (event.key === 'Enter') {
      updateNavigationTree(event, indexes)
    }
  }

  useEffect(() => {
    if (isSearchOpen) {
      // TODO forward ref to avoid querying DOM
      const searchInput = document.querySelector('input[name="sp_q"]')
      if (searchInput instanceof HTMLInputElement) {
        searchInput.focus()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSearchOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSearchOpen])

  const handleMenuClick = (event: React.MouseEvent, indexes: number[]) => {
    updateNavigationTree(event, indexes)
  }

  if (!isOpen) {
    return null
  }

  return (
    <div
      className={`AppMenuMobile u-block u-flexAlignSelfStretch u-bgColorLight u-borderTop u-borderColorNeutral ${className}`}
      ref={mobileNavSectionRef}
    >
      <ul className="AppMenuMobile-tier1 AppMenuMobile-menuItems List List--selectable u-borderBottom0 u-margin0 u-bgColorWhite">
        {(navigationTree[0] !== undefined || isSearchOpen) && (
          <li>
            <button
              className="Link AppMenuMobile-backButton u-linkClean u-flex u-sizeFull u-paddingVert3gu u-paddingLeft10gu u-paddingRight4gu u-bgColorWhite u-borderVert"
              onClick={handleBackClick}
              data-testid={testId ? `${testId}-back-button` : null}
            >
              <span className="u-marginLeft1gu u-textSizeMsrm1">Back</span>
            </button>
          </li>
        )}
        {!isSearchOpen && menuItems && (
          <>
            {menuItems.map(
              (tier1Item, tier1Index) =>
                (navigationTree[0] === undefined ||
                  navigationTree[0] === tier1Index) && (
                  <AppMenuMobileMenuItem
                    key={tier1Item.name}
                    tier1Item={tier1Item}
                    tier1Index={tier1Index}
                    navigationTree={navigationTree}
                    handleMenuClick={handleMenuClick}
                    handleMenuKeyDown={handleMenuKeyDown}
                  />
                )
            )}
          </>
        )}
      </ul>
      {searchPanel && isSearchOpen && (
        <div className="u-sizeConstrained">
          {searchPanel.searchBox && (
            <div className="u-paddingTop9gu">
              <SearchBox {...searchPanel.searchBox} />
            </div>
          )}
          {searchPanel.mostVisitedLinks && (
            <div className="u-paddingTop14gu">
              <MostVisitedLinks {...searchPanel.mostVisitedLinks} />
            </div>
          )}
        </div>
      )}
      {!isSearchOpen && (
        <div className="u-sizeConstrained">
          <ul className="List List--selectable u-borderBottom0 u-margin0 u-sm-paddingVert3gu u-sm-paddingHoriz3gu u-md-paddingVert5gu u-md-paddingHoriz3gu u-textCenter">
            {searchPanel?.searchBox && (
              <li className="List-item">
                <button
                  style={{ width: '100%', cursor: 'pointer' }}
                  className="u-block u-textCenter u-textSizeMsrm1 u-textColorActionBlue"
                  onClick={handleSearchClick}
                >
                  <MaterialIcon
                    className="u-marginRight2gu"
                    style={{ paddingTop: '3px' }}
                    icon={MaterialIconId.SEARCH}
                    size={MaterialIconSize.SIZE_16}
                    ariaLabel="search"
                  />
                  Search
                </button>
              </li>
            )}
            {mobileBottomMenuItems?.map((navItem, index) => (
              <li key={index} className="List-item ">
                <Link
                  {...navItem}
                  url={makeUrlRelative(navItem.url)}
                  className="u-block u-textCenter u-sm-textSizeMsrm1 u-md-textSizeMsrm1"
                />
              </li>
            ))}

            {userStatus === UserStatus.CLIENT_ACTIVE &&
              userData?.data?.client?.user?.clFirstNm && (
                <li className="List-item">
                  <Link
                    name={`Hi, ${userData.data.client.user.clFirstNm}`}
                    url={makeUrlRelative(
                      'https://www.ameriprise.com/secure/profile'
                    )}
                    analyticsName="Secure Profile"
                    className="u-block u-textCenter u-sm-textSizeMsrm1 u-md-textSizeMsrm1"
                    testId={testId ? `${testId}-secure-profile` : undefined}
                  />
                </li>
              )}

            {userStatus !== UserStatus.CLIENT_ACTIVE && (
              <li className="List-item">
                <Link
                  name="Log In"
                  url={makeUrlRelative(
                    'https://www.ameriprise.com/client-login'
                  )}
                  analyticsName="Log In"
                  className="u-block u-textCenter u-sm-textSizeMsrm1 u-md-textSizeMsrm1"
                  testId={testId ? `${testId}-log-in` : undefined}
                />
              </li>
            )}
          </ul>

          <div className="u-marginLeft4gu u-marginRight4gu">
            {userStatus === UserStatus.PROSPECT && advisorData?.url && (
              <>
                <hr className="Rule u-lg-hidden u-marginBottom6gu" />
                <p className="u-marginBottom6gu u-textCenter u-sm-textSizeMsrm1">
                  Take the first step to a confident financial future. Contact{' '}
                  <b>{advisorData?.name}</b>
                </p>
                <Link
                  name="Request Consultation"
                  url={advisorData?.url}
                  variant={LinkVariant.PRIMARY}
                  className="u-sizeFull"
                  testId={testId ? `${testId}-request-consultation` : undefined}
                />
              </>
            )}
            {userStatus === UserStatus.CLIENT_ACTIVE && (
              <Link
                name="Return to My Accounts"
                url={makeUrlRelative(
                  'https://www.ameriprise.com/secure/overview'
                )}
                analyticsName="Return to My Accounts"
                variant={LinkVariant.SECONDARY}
                className="Button--block u-flexJustifyCenter u-marginTop10gu"
                testId={testId ? `${testId}-return-accounts` : undefined}
              />
            )}
            {userStatus === UserStatus.PROSPECT && !advisorData?.url && (
              <Link
                name="Find an Advisor"
                url="https://www.ameripriseadvisors.com"
                analyticsName="Find an Advisor"
                variant={LinkVariant.PRIMARY}
                className="Button--block u-flexJustifyCenter u-marginTop10gu"
                testId={testId ? `${testId}-find-advisor` : undefined}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
